<template>
  <!-- CONTENT -->
  <div id="specimen-results-wrapper" class="cytology-wrapper">
    <transition name="modal-fade">
      <SlideImagesPopup
        v-if="isSlideImagesOpen"
        title="Results"
        :specimenId="slideModalState.specId"
        :popupVisible.sync="isSlideImagesOpen"
        :data="slideModalState.data"
      ></SlideImagesPopup>
    </transition>
    <div class="specimen-section__header d-flex justify-content-between">
      <div ref="pageTitle" class="d-inline-flex p-1 align-items-center">
        <h1 class="font-weight-semibold mb-0">Results</h1>
        <transition name="fade-fast">
          <loader size="small" v-if="isLoading" class="ml-2" />
        </transition>
        <transition name="slide-in" mode="out-in">
          <div v-if="editType" key="edit-section" class="d-flex ml-3">
            <div class="align-self-center">
              <h2 class="specimen-section__title mb-0">{{ currentEditType }}</h2>
            </div>
            <div class="align-self-center ml-2">
              <icon-button
                icon="pen-alt"
                class="btn btn-outline-info specimen-section__header-icon"
                @click="toggleReReport"
              />
            </div>
          </div>
        </transition>
      </div>
    </div>
    <div class="cytology-form-container">
      <CytologyResult
        ref="resultForm"
        :isLoading="isLoading"
        v-model="selectedSpecimen"
        @save="submitResults"
        @delay="saveAndDelay"
        :isUnlocked="isUnlocked"
      />
    </div>
  </div>
</template>
<script>
import SpecimenService from "../services/specimen";
import DropdownService from "../services/dropdown";
import MacrosService from "../services/macros";
import ProviderAPI from "@/services/providers";
import { mapGetters, mapState } from "vuex";
import defaultSpecimen from "../modules/specimen";
import { debounce } from "lodash";
import dialog from "@/modules/dialog";
import {
  getTextFromHtml,
  buildTitle,
  altKey,
  getEditTypes,
  getCaseProstateData,
  createLogItem
} from "../modules/helpers";
import Loader from "@/components/common/Loader.vue";
import physicianPopup from "@/mixins/physicianPopup";
import ArrayStore from "devextreme/data/array_store";
import eventBus, {
  LOCK_EDITORS,
  PRESSED_SIGNOUT_KEY,
  UNLOCK_EDITORS,
  OPEN_SLIDE_IMAGES,
  LAST_SPECIMEN_SAVED
} from "@/modules/eventBus";
import { handleErrors } from "@/modules/handleErrors";
import { AuditLogApi, CasesApi } from "@/services";
import launchImage from "@/modules/launchImage";
import IconButton from "@/components/common/IconButton.vue";
import SlideImagesPopup from "@/components/common/SlideImagesPopup.vue";
import CytologyResult from "@/components/forms/Accession/CytologyResult.vue";
import cytologyResultService from "@/services/cytresults";
import auditLog from "@/services/AuditLog";
import { CytResultTypeEnum, PhysicianPopupsEnum } from "@/modules/enums";

export default {
  components: {
    Loader,
    IconButton,
    SlideImagesPopup,
    CytologyResult
  },
  mixins: [physicianPopup],
  data() {
    return {
      isSlideImagesOpen: false, //Added by IP-1095
      slideModalState: {
        data: [],
        specId: null
      },
      scrolling: {
        showScrollbar: "always",
        useNative: true
      },
      slideWindows: {},
      selection: { mode: "single" },
      accessionInfo: {},
      selectedGCode: null,
      suggestions: [],
      inputProps: {
        class: "form-control"
      },
      editTypes: getEditTypes(),
      selected: null,
      sectionConfigs: {
        icd: {
          onSelected: selected => {
            this.selected = selected.item;
          }
        }
      },
      macroHeader: { header: "Results Macros", classes: {}, checked: false },
      macros: [],
      timeout: null,
      protocolOptions: [],
      icdOptions: [],
      cptOptions: [],
      siteOptions: [],
      specimenSlideImageUrl: null,
      resultMacrosOptions: [],
      diagnosisSummaries: [],
      defaultSpecimen: defaultSpecimen,
      test: "",
      originalSpecimens: [],
      focusedRowIndex: undefined,
      selectedSpecimen: null,
      selectedIndex: null,
      specimenGrid: {},
      dropdownSpecimenId: null,
      isUnlocked: false,
      routeLeaveCalled: false,
      shortkeySite: altKey("i"),
      shortkeySlideImage: altKey("-"),
      signAfterSave: false,
      staticCaseId: null
    };
  },
  created() {
    const { labPrefix, caseId } = this.caseDetails;
    DropdownService.getProtocol(labPrefix).then(res => {
      this.protocolOptions = res || [];
    });
    this.$store.dispatch("dropdowns/getBodyParts");
    if (this.isCytology) {
      if (this.specimens?.length) {
        // CYTOLOGY WILL ONLY HAVE 1 SPECIMEN
        this.setSelected(this.specimens[0], 0);
      }
    }
    if (this.isMolecularTestOnly) {
      this.$router.push("/accession/" + caseId);
    }
  },

  computed: {
    ...mapState({
      labSettings: state => state.labSettings,
      currentUser: state => state.currentUser,
      currentUserPathologistId: state => state.currentUser.pathologistId,
      token: state => state.token,
      currentLab: state => state.currentLab,
      reasonForChange: state => state.accessionStore.reasonForChange,
      editType: state => state.accessionStore.editType,
      caseDetails: state => state.accessionStore.caseDetails,
      specimens: state => state.accessionStore.specimens,
      isLoading: state => state.accessionStore.loading,
      currentSpecimen: state => state.accessionStore.currentSpecimen,
      textEditors: state => state.applicationSettings.textEditors,
      bodyPartOptions: state => state.dropdowns.bodyParts,
      specimenToLoad: state => state.accessionStore.specimenToLoad,
      caseHeader: state => state.accessionStore.caseHeader,
      slideImages: state => state.accessionStore.slideImages,
      viewedSlideImages: state => state.sessionDetails.viewedSlideImages,
      autoOpenSlideImages: state => state.applicationSettings.autoOpenSlideImages,
      SlideImagesApplicationHandler: state => state.labSettings.SlideImagesApplicationHandler,
      OpenFirstSlideImage: state => state.labSettings.OpenFirstSlideImage,
      shouldPrintOrders: state => state.accessionStore.shouldPrintOrders,
      autoOpenSpecimenResults: state => state.applicationSettings.autoOpenSpecimenResults,
      isPathologist: state => state.currentUser.isPathologist,
      cytologyNoSignoutHoldCodeId: state => state.labSettings.CytologyNoSignoutHoldCodeId,
      caseHolds: state => state.accessionStore.caseHolds
    }),
    ...mapGetters("accessionStore", [
      "caseStatus",
      "primaryPathologist",
      "isCytology",
      "isMolecularTestOnly"
    ]),
    isLoading: {
      get() {
        return this.$store.state.accessionStore.loading;
      },
      set(value) {
        this.$store.commit("accessionStore/setLoading", value);
        return value;
      }
    },
    accordionView: {
      get() {
        return this.$store.state.applicationSettings.accordionView;
      },
      set(value) {
        this.$store.commit("applicationSettings/setAccordionView", value);
        return value;
      }
    },
    selectedSpecimenImages() {
      if (this.selectedSpecimen?.id && this.slideImages?.length > 0) {
        return this.slideImages.find(e => e.specimenId === this.selectedSpecimen.id);
      }
      return null;
    },
    slideImagesDataSource() {
      if (this.selectedSpecimenImages !== null) {
        const imageUrls = [];
        if (this.selectedSpecimenImages.slideImageUrls) {
          imageUrls.concat(this.selectedSpecimenImages);
        }
        if (this.selectedSpecimenImages.specimenSlideImageUrl) {
          imageUrls.push(this.selectedSpecimenImages.specimenSlideImageUrl);
        }
        return { imageUrls };
      }
      return [];
    },

    specimensDataSource() {
      return new ArrayStore({
        data: this.specimens,
        key: "specimenOrder"
      });
    },
    currentEditType() {
      const editType = this.editTypes.find(type => type.id === this.editType);
      if (editType) {
        return editType.displayName;
      }
      return "";
    },
    slideImagesColumns() {
      return [
        {
          dataField: "type",
          dataType: "string",
          caption: "Type"
        },
        {
          dataField: "Action",
          dataType: "string",
          caption: "Action",
          cellTemplate: "actions"
        }
      ];
    },
    columns() {
      return [
        {
          dataField: "specimenOrder",
          dataType: "string",
          caption: "Specimen",
          width: "4.75rem"
        },
        {
          caption: "Protocol",
          dataType: "number",
          dataField: "protocolId",
          lookup: {
            dataSource: this.protocolOptions,
            valueExpr: "id",
            displayExpr: "displayName"
          },
          width: "10rem"
        },
        {
          caption: "Site",
          dataType: "string",
          calculateCellValue: this.calcSite,
          width: "10rem"
        },
        {
          dataField: "icdCodes",
          caption: "ICD10 Codes",
          calculateCellValue: data => this.calculateIcdDisplay(data),
          width: "6rem"
        },
        {
          dataField: "diagnosis",
          dataType: "string",
          calculateCellValue: data => data.diagnosis && getTextFromHtml(data.diagnosis),
          minWidth: "250px",
          width: "40%",
          cssClass: "truncate"
        },
        {
          dataField: "macroIsModified",
          dataType: "boolean",
          caption: "Modified",
          lookup: {
            dataSource: [
              { displayName: "Yes", id: true },
              {
                displayName: "No",
                id: false
              }
            ],
            displayExpr: "displayName",
            valueExpr: "id"
          },
          cellTemplate: "isModifiedTemplate",
          width: "4.5rem"
        },
        {
          dataField: "resultsMacros",
          calculateCellValue: data =>
            data.resultsMacros
              .map(e => e.displayName)
              .join(", ")
              .toUpperCase(),
          visible: false
        }
      ];
    },
    selectedSpecimenOrder() {
      if (this.selectedSpecimen) {
        return [this.selectedSpecimen.specimenOrder];
      }
      return [];
    },
    currentSpecimenOrder() {
      if (this.selectedSpecimen) {
        return this.selectedSpecimen.specimenOrder;
      }
      return [];
    },
    mappedProtocol() {
      return this.protocolOptions.reduce(
        (obj, item) =>
          Object.assign(obj, {
            [item.id]: item.displayName
          }),
        {}
      );
    },
    mappedBodyPart() {
      return this.bodyPartOptions.reduce(
        (obj, item) =>
          Object.assign(obj, {
            [item.id]: item.displayName
          }),
        {}
      );
    },
    mappedSite() {
      return this.siteOptions.reduce(
        (obj, item) =>
          Object.assign(obj, {
            [item.id]: item.displayName
          }),
        {}
      );
    },
    hasCytologyHold() {
      if (!this.caseHolds?.length) {
        return false;
      }
      const holdCodes = this.caseHolds.filter(hold => {
        return hold.tagId === this.cytologyNoSignoutHoldCodeId;
      });

      return holdCodes.length > 0;
    }
  },
  watch: {
    selectedSpecimen: {
      deep: true,
      handler(nv) {
        this.$store.commit("accessionStore/setCurrentSpecimen", nv ?? {});
        this.dropdownSpecimenId = nv?.id || null;
      }
    },
    dropdownSpecimenId: {
      handler(nv) {
        if (nv !== this.selectedSpecimen?.id) {
          const newSpecimenIndex = this.specimens.findIndex(e => e.id === nv);
          this.setSelected(this.specimens[newSpecimenIndex], newSpecimenIndex);
        }
      }
    },
    "caseDetails.caseId": {
      immediate: true,
      handler(nv) {
        if (nv) {
          this.staticCaseId = parseInt(nv);
        }
      }
    }
  },
  beforeDestroy() {
    this.autoPrintOrders();
    eventBus.$off(PRESSED_SIGNOUT_KEY);
    eventBus.$off(OPEN_SLIDE_IMAGES);
  },
  methods: {
    getSpecimenById(specId) {
      return this.specimens.find(e => e.id === specId);
    },
    launchImage(data) {
      launchImage(data.url, this.SlideImagesApplicationHandler);
    },
    toggleReReport() {
      this.$store.commit("setReReportPopup", !this.$store.state.accessionStore.reReportPopup);
    },
    handleViewPathReport() {
      const { caseId } = this.$route.params;
      this.$store.dispatch("report/viewPathReport", { caseId });
    },
    hasSlideImages(specId) {
      if (this.slideImages?.length > 0) {
        const slides = this.slideImages.find(e => e.specimenId === specId);
        return slides?.slideImageUrls?.length > 0 || slides?.specimenSlideImageUrl;
      }
      return false;
    },
    generateImageUrls(specId) {
      if (!specId || this.slideImages?.length === 0) {
        return [];
      }
      const slides = this.slideImages.find(e => e.specimenId === specId);
      const imageUrls = [
        ...(slides.slideImageUrls || []).map(e => ({
          type: "Slide",
          url: e.slideImageUrl,
          blockNum: e.blockNumber,
          slideLabel: e.slideLabel,
          slideProcedure: e.slideProcedure
        }))
      ];
      if (slides.specimenSlideImageUrl) {
        imageUrls.push({ type: "Specimen", url: slides.specimenSlideImageUrl });
      }
      return imageUrls;
    },
    setGridInstance({ component }) {
      this.specimenGrid = component;
    },
    checkMacroContent: debounce(async function (specimen) {
      let header = "Results Macros";
      let classes = "";
      if (specimen && specimen.resultsMacros.length) {
        const macros = await Promise.all(
          specimen.resultsMacros.map(e => MacrosService.getMacroDetails(e.id))
        );
        for (const macro of macros) {
          if (!macro.microscopic && header != "Results Macros (G)") {
            header = "Results Macros (G)";
          }
        }
      }
      return (this.macroHeader = { header, classes, checked: true });
    }, 500),
    setPrimaryContact(id) {
      this.selectedSpecimen.contact = this.selectedSpecimen.contact.map(entry => {
        if (entry?.contactId === id) {
          entry.isPrimary = true;
        } else {
          entry.isPrimary = false;
        }
        return entry;
      });
    },
    fetchContacts(searchValue) {
      let options = {
        sortFields: [
          {
            field: "Provider.LastName",
            ascending: true
          }
        ],
        searchPaths: ["Provider.FirstName", "Provider.LastName", "ProviderAddress.OfficeName"],
        searchPathsInclusive: true,
        searchValue
      };
      return ProviderAPI.searchContacts(options);
    },
    appendMacro(macro) {
      if (macro.macroType != 0) {
        return;
      }
      if (this.selectedSpecimen) {
        this.selectedSpecimen.resultsMacros.push({
          id: macro.macroId,
          displayName: macro.macroName,
          isDeleted: false
        });
      }
    },
    removeSelectedMacro(specimen, index) {
      if (specimen) {
        specimen.resultsMacros.splice(index, 1);
      }
    },
    getSpecimens(id) {
      return SpecimenService.getSpecimen(id).then(response => {
        this.specimens =
          response?.map(val => ({
            ...val,
            blockNum: val.cassettes ? val.cassettes.length : 0,
            gross: val.gross ? val.gross : "",
            clinical: val.clinical ? val.clinical : "",
            interfaceDiagnosis: val.interfaceDiagnosis ? val.interfaceDiagnosis : "",
            diagnosis: val.diagnosis ? val.diagnosis : "",
            microscopic: val.microscopic ? val.microscopic : "",
            notes: val.notes ? val.notes : "",
            caseNotes: val.caseNotes ? val.caseNotes : "",
            initIcdCodes: val.icdCodes ? val.icdCodes : [],
            initCptCodes: val.cptCodes ? val.cptCodes : []
          })) || [];
        this.originalSpecimens = [...this.specimens];
      });
    },
    async setSelected(specimen, index) {
      if (this.selectedSpecimen) {
        const isFormDirty = this.checkForChanges();
        if (isFormDirty) {
          const confirm = await dialog(
            "You may have unsaved data. Would you like to continue without saving? Any changes you have made will be lost.",
            "IntelliPath Pro",
            "Yes",
            "No",
            false
          ).show();
          if (!confirm) {
            this.dropdownSpecimenId = this.currentSpecimen.id;
            return;
          }
        }
      }
      if (index === this.selectedIndex) {
        this.selectedSpecimen = null;
        this.selectedIndex = null;
      } else {
        this.selectedSpecimen = specimen;
        this.selectedIndex = index;
      }
    },
    buildTitle,
    submitResults(specimen) {
      return this.resultCytologySpecimen(specimen);
    },
    async saveAndDelay(specimen) {
      try {
        const caseId = this.caseDetails.caseId;
        this.isLoading = true;
        if (this.hasCytologyHold) {
          const holdCode = this.caseHolds.find(hold => {
            return hold.tagId === this.cytologyNoSignoutHoldCodeId;
          });
          await this.$store.dispatch("accessionStore/removeQuickLink", {
            type: "H",
            caseId,
            ids: [holdCode.id]
          });
          const logItem = createLogItem(this.caseDetails, 1);
          logItem.comments = `Removed a hold code cytology no signout hold code, \n${JSON.stringify(
            this.cytologyNoSignoutHoldCodeId,
            null,
            2
          )}`;
          await AuditLogApi.insertLogMessage(logItem);
        } else {
          await this.resultCytologySpecimen(specimen);
          const hold = {
            type: "H",
            tagId: this.cytologyNoSignoutHoldCodeId,
            caseId,
            text: `Added for save & delay`
          };
          await this.$store.dispatch("accessionStore/upsertCaseQuickLink", hold);
          const logItem = createLogItem(this.caseDetails, 1);
          logItem.comments = `Added a hold code, \n${JSON.stringify(hold, null, 2)}`;
          AuditLogApi.insertLogMessage(logItem);
        }
        await this.$store.dispatch("accessionStore/getCaseQuickLinks", caseId);
        await this.$store.dispatch("accessionStore/getCaseHeader", caseId);
      } catch (error) {
        this.isLoading = false;
        console.log(error);
        window.notify("An error occurred while saving the case.", "error");
      } finally {
        this.isLoading = false;
      }
    },
    async saveAndContinue(specimen) {
      this.$store.commit("accessionStore/setLoading", true);
      try {
        await this.$store.dispatch("accessionStore/resultSingleSpecimen", specimen);
      } catch (e) {
        window.notify("Error saving single specimen.", "error");
        this.dropdownSpecimenId = specimen.id;
        const prevSpecimenIndex = this.specimens.findIndex(e => e.id === specimen.id);
        this.selectedSpecimen = specimen;
        this.selectedIndex = prevSpecimenIndex;
        this.$store.commit("accessionStore/setLoading", false);
        return;
      }
      this.$store.commit("accessionStore/setLoading", false);
      window.notify(`Success`, "success", 2000, { at: "center", of: "#navbar" });
      this.handleViewPathReport(specimen.caseId);
    },
    async resultCytologySpecimen(specimen) {
      const { cytResults, cytQtySlides, ...rest } = specimen;
      const specimens = [rest];
      this.$store.commit("accessionStore/setLoading", true);
      let success = false;

      try {
        const confirmStatus = await this.getPhysicianPopup(PhysicianPopupsEnum.Specimen, specimens); //Specimen Save
        if (!confirmStatus) {
          this.isLoading = false;
          window.notify("User cancelled action.", "error");
          return;
        }
        let caseUpdate = false;
        const caseDetails = {
          ...this.caseDetails
        };
        if (cytResults.cytResultTypeId != CytResultTypeEnum.Rescreening) {
          //! TODO Determine if other updates should go update.
          if (this.isPathologist) {
            //Primary Pathologist captured.
            rest.pathologist = [
              {
                id: this.currentUserPathologistId,
                isPrimary: true
              }
            ];
            await this.$store.dispatch("accessionStore/resultSingleSpecimen", rest);
            caseDetails.pathologists = [
              {
                id: this.currentUserPathologistId,
                isPrimary: true
              },
              ...(caseDetails.pathologists || []).filter(
                p => p.id !== this.currentUserPathologistId
              )
            ];
            caseUpdate = true;
          }

          if (cytQtySlides != undefined && cytQtySlides != null) {
            caseDetails.cytQtySlides = cytQtySlides;
            caseUpdate = true;
          }

          if (caseUpdate) {
            await this.$store.dispatch("accessionStore/updateCaseDetails", {
              ...caseDetails
            });
          }
        }

        // Save the cytology results
        if (cytResults) {
          const payload = {
            ...cytResults,
            specimenId: rest.id,
            agreesWithPrevious:
              cytResults.cytResultTypeId != CytResultTypeEnum.Rescreening
                ? false
                : cytResults.agreesWithPrevious
          };

          await cytologyResultService.create(payload);
          const logItem = createLogItem(this.caseDetails, 1);
          logItem.comments = JSON.stringify(
            {
              specimenId: rest.id,
              cytologyResults: cytResults,
              agrees:
                cytResults.cytResultTypeId != CytResultTypeEnum.Rescreening
                  ? false
                  : cytResults.agreesWithPrevious
            },
            null,
            2
          );
          await auditLog.insertLogMessage(logItem);
          const formRef = this.$refs.resultForm;
          if (formRef) {
            formRef.loadCytResults(false);
          }
        }

        window.notify(`Success`, "success", 2000, { at: "center", of: "#navbar" });
        success = true;
        // Handle the next specimen
        eventBus.$emit(LAST_SPECIMEN_SAVED);
        await this.$store.dispatch("accessionStore/getCaseHeader", this.caseDetails.caseId);
      } catch (error) {
        console.log(error);
        handleErrors(error);
      } finally {
        if (success) {
          this.$store.dispatch("logTimeSinceBase", "** Total save time from click. **");
          // If the case is not resulted yet, we will try to move the user to the next specimen.
          await CasesApi.getCaseStatus(this.caseDetails.caseId);

          this.handleViewPathReport(this.$route.params.caseId);
        }
        this.$store.commit("accessionStore/setLoading", false);
      }
    },
    onSelectionChanged(e) {
      const { rowIndex, data } = e;
      this.setSelected(data, rowIndex);
    },
    calcProtocol(data) {
      let protocol = this.mappedProtocol[data.protocolId] || "";
      return protocol;
    },
    calcSite(data) {
      let bodyPart = this.mappedBodyPart[data.bodyPartId]
        ? this.mappedBodyPart[data.bodyPartId] + ", "
        : "";
      let site = data.site ? data.site : "";
      return [bodyPart, site].join("");
    },
    getSpecimenRichText(specimen) {
      return {
        diagnosis: specimen.diagnosis ? getTextFromHtml(specimen.diagnosis) : "",
        caseNotes: specimen.caseNotes ? getTextFromHtml(specimen.caseNotes) : "",
        gross: specimen.gross ? getTextFromHtml(specimen.gross) : "",
        clinical: specimen.clinical ? getTextFromHtml(specimen.clinical) : "",
        notes: specimen.notes ? getTextFromHtml(specimen.notes) : "",
        microscopic: specimen.microscopic ? getTextFromHtml(specimen.microscopic) : ""
      };
    },
    handleShortKey() {
      if (this.specimens.length === 1) {
        if (this.dropdownSpecimenId !== this.specimens[0].id) {
          this.dropdownSpecimenId = this.specimens[0].id;
        } else {
          this.dropdownSpecimenId = null;
        }
        return;
      }
      if (!this.$refs.specDropdown.isFocused) {
        this.$refs.specDropdown.focus();
      } else {
        this.$refs.specDropdown.focusOut();
        const specFormRef =
          this.$refs?.specimenForm?.$refs?.site || this.$refs.specimenForm[0].$refs.site;
        this.$nextTick(() => {
          if (specFormRef) {
            specFormRef.focus();
          }
        });
      }
    },
    toggleLock() {
      if (this.isUnlocked) {
        this.$store.dispatch("applicationSettings/setUserSettings", {
          textEditors: this.textEditors
        });
        eventBus.$emit(LOCK_EDITORS);
      } else {
        eventBus.$emit(UNLOCK_EDITORS);
      }
      this.isUnlocked = !this.isUnlocked;
    },
    async checkSpecimens() {
      if (this.selectedSpecimen) {
        const isFormDirty = this.checkForChanges();
        if (isFormDirty && this.token) {
          const confirm = await dialog(
            "You may have unsaved data. Unsaved will be lost if you proceed.<br> <span class='mx-auto'>Are you sure?</span>",
            "IntelliPath Pro",
            "Yes",
            "No"
          ).show();
          if (!confirm) {
            return false;
          }
        }
      }
      return true;
    },
    checkForChanges() {
      const getFields = checkedObject => {
        const fieldsToCheck = [
          "pathologists",
          "resultsMacros",
          "icdCodes",
          "prostateLength",
          "prostatePieces",
          "gleasonScore",
          "tumorSize",
          "tables"
        ];
        let returnObject = {};
        for (const field of fieldsToCheck) {
          returnObject[field] = checkedObject[field];
        }
        returnObject = {
          ...returnObject,
          ...getCaseProstateData(checkedObject),
          ...this.getSpecimenRichText(checkedObject)
        };
        return returnObject;
      };
      if (this.selectedSpecimen) {
        const originalSpecimen = {
          ...getFields(this.selectedSpecimen)
        };
        const currentSpecimen = {
          ...getFields(this.currentSpecimen)
        };
        return JSON.stringify(originalSpecimen) !== JSON.stringify(currentSpecimen);
      } else {
        return false;
      }
    },

    calculateIcdDisplay({ icdCodes }) {
      if (!icdCodes?.length) {
        return "";
      }
      return icdCodes.map(e => e.displayCode).join(", ");
    },
    autoPrintOrders() {
      if (this.shouldPrintOrders) {
        this.$store.commit("accessionStore/setShouldPrintOrders", false);
        this.$store.dispatch("accessionStore/autoPrintOrders", this.staticCaseId);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
pre {
  outline: 1px solid #ccc;
  padding: 5px;
  margin: 5px;
}

.cytology-wrapper {
  margin: 0 auto;
  padding: 2rem 1rem;
  width: 100%;
  min-height: 40dvh;
}

/* Header section styling */
.specimen-section__header {
  min-height: 60px;
  transition: all 0.3s ease;
}

/* Form container with smooth transitions */
.cytology-form-container {
  transition: opacity 0.3s ease;
}

/* Loader transition */
.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.2s ease;
}
.fade-fast-enter,
.fade-fast-leave-to {
  opacity: 0;
}

/* Edit section slide transition */
.slide-in-enter-active,
.slide-in-leave-active {
  transition: all 0.3s ease;
}
.slide-in-enter {
  opacity: 0;
  transform: translateX(-20px);
}
.slide-in-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

/* Modal transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

::v-deep span.string {
  color: green;
}

::v-deep .number {
  color: darkorange;
}

::v-deep .boolean {
  color: blue;
}

::v-deep .null {
  color: magenta;
}

::v-deep .key {
  color: red !important;
}
::v-deep .yellow .layout-container {
  width: 100%;
}

.multiselect__single {
  background: unset;
  color: unset;
  padding: unset;
  width: unset;
}

.specimen-section {
  &__header {
    display: flex;
    align-items: center;
  }

  &__title {
    transition: all 0.3s ease;
  }

  &__header-icon {
    color: $primary;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  &__header-btn {
    margin-left: auto !important;
  }
}

.small-input {
  margin-left: 20px;
  width: 50px;
}

.tag-btn {
  padding: 0px;
  margin: 0px;
}

.icd-col {
  padding-top: 2px;
}
.icd-select {
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin: 0 !important;
}

.custom__icdtag {
  border: white solid 1px;
}

.icd-select >>> .multiselect__content-wrapper {
  width: 500px !important;
}

.results-header {
  background: #e6e6e6;
  padding: 5px 25px;
  margin: 0px auto;
}

.general-information {
  border: 1px solid #dee2e6 !important;
  border-radius: 0.25rem;
  padding: 5px 25px;
  margin: 10px auto;
}
.tag-input {
  width: 100%;
  border: 1px solid #eee;
  font-size: 1rem;
  font-weight: 500;
  height: 50px;
  box-sizing: border-box;
  padding: 0 10px;
  border-radius: 0.25rem;
}

.tag-input__tag > span {
  cursor: pointer;
  opacity: 0.75;
}
::v-deep .gross-col {
  text-overflow: ellipsis;
}
::v-deep
  .dx-data-row.dx-state-hover:not(.dx-selection):not(.dx-row-inserted):not(.dx-row-removed):not(.dx-edit-row)
  > td:not(.dx-focused) {
  cursor: pointer;
}
</style>
